import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import DOMPurify from "dompurify";
import axios from 'axios';

const CopywritingPhone = () => {
  const [copywritingsPhones, setCopywritings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Componente seguro para renderizar HTML do backend
  const SafeHTML = ({ htmlContent }) => {
    const sanitizedHTML = DOMPurify.sanitize(htmlContent, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'span', 'a'],
      ALLOWED_ATTR: ['href', 'target', 'class', 'style']
    });
    
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="safe-content"
        style={{
          color: '#475569',
          fontSize: '1rem',
          lineHeight: 1.8,
          maxWidth: '800px',
          margin: '0 auto',
        }}
        dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
      />
    );
  };

  useEffect(() => {
    fetchCopywritings();
  }, []);

  const fetchCopywritings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/copywritingsPhone`
      );
      setCopywritings(response.data);
    } catch (error) {
      console.error("Erro ao buscar os CopywritingPhone:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <motion.div
          animate={{ 
            rotate: 360,
            borderColor: ['#F62690', '#7A00CC', '#F62690']
          }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
          style={{
            width: '50px',
            height: '50px',
            border: '4px solid',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            margin: '0 auto',
          }}
        />
      </div>
    );
  }

  return (
    <div className="position-relative overflow-hidden">
      {/* Background decoration */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.03 }}
        transition={{ duration: 1 }}
        className="position-absolute"
        style={{
          top: '-20%',
          right: '-10%',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, #F62690 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)',
          zIndex: 0,
        }}
      />

      <div className="position-relative z-1">
        {copywritingsPhones.map((copywritingPhone, index) => (
          <motion.div
            key={copywritingPhone._id}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: 0.6, 
              delay: index * 0.1,
              type: 'spring',
              stiffness: 100
            }}
            className="text-center py-5"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill mb-4"
              style={{
                background: 'linear-gradient(135deg, rgba(246, 38, 144, 0.1), rgba(122, 0, 204, 0.1))',
                border: '1px solid rgba(246, 38, 144, 0.3)',
                color: '#F62690',
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                style={{ color: '#7A00CC' }}
              >
                <i className="fa fa-phone"></i>
              </motion.span>
              Solução VoIP Empresarial
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="display-5 fw-bold mb-3"
              style={{
                background: 'linear-gradient(135deg, #1e1b4b 0%, #4c1d95 50%, #1e1b4b 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
              }}
            >
              {copywritingPhone.title || 'Telefonia Fixa via Internet'}
            </motion.h1>

            {/* Subtitle */}
            <motion.h5
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-4"
              style={{
                color: '#7A00CC',
                fontWeight: 600,
                fontSize: '1.25rem',
                position: 'relative',
                display: 'inline-block',
              }}
            >
              {copywritingPhone.sub || 'Conheça nosso PABX virtual. Profissionalize o atendimento da sua empresa!'}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                style={{
                  position: 'absolute',
                  bottom: '-4px',
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: 'linear-gradient(90deg, #F62690, #7A00CC)',
                  borderRadius: '2px',
                  transformOrigin: 'left',
                }}
              />
            </motion.h5>

            {/* Description */}
            <SafeHTML htmlContent={copywritingPhone.text} />

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-4"
            >
              <motion.button
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: '0 10px 30px rgba(246, 38, 144, 0.3)'
                }}
                whileTap={{ scale: 0.98 }}
                style={{
                  padding: '0.875rem 2rem',
                  background: 'linear-gradient(135deg, #F62690, #7A00CC)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50px',
                  fontWeight: 600,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <i className="fas fa-headset"></i>
                <a href="/contato" style={{textDecoration: 'none', color: '#FFF'}}>Falar com Especialista</a>                
              </motion.button>
            </motion.div>

            {/* Trust badges */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="d-flex justify-content-center gap-4 mt-5 flex-wrap"
            >
              {[
                { icon: 'fa fa-wifi', text: 'Só precisa de internet' },
                { icon: 'fa fa-usd', text: 'Economia de até 70%' },
                { icon: 'fa fa-bolt', text: 'Ativação rápida' },
              ].map((badge, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ y: -3 }}
                  className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill"
                  style={{
                    background: 'rgba(122, 0, 204, 0.05)',
                    border: '1px solid rgba(122, 0, 204, 0.1)',
                    color: '#7A00CC',
                    fontSize: '0.875rem',
                  }}
                >
                  <i className={badge.icon}></i>
                  <span>{badge.text}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        ))}
      </div>

      {/* Additional CSS */}
      <style>{`
        .safe-content p {
          margin-bottom: 1rem;
        }
        .safe-content strong {
          color: #7A00CC;
          font-weight: 600;
        }
        .safe-content a {
          color: #F62690;
          text-decoration: none;
          border-bottom: 1px solid transparent;
          transition: border-color 0.3s;
        }
        .safe-content a:hover {
          border-bottom-color: #F62690;
        }
      `}</style>
    </div>
  );
};

export default CopywritingPhone;